import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchChildren, fetchSenderRules, createSenderRule, deleteSenderRule,
  createChild, deleteChild, loadDemoFamily, resetMyDemo,
} from "@/lib/api";
import { CHILDREN } from "@/constants/testIds";
import { childChip } from "@/lib/format";
import {
  Users, Plus, Trash2, ShieldOff, GraduationCap, Trophy, Tent, Music,
  UserPlus, Pencil, Sparkles, RefreshCcw, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import UpgradeModal from "@/components/UpgradeModal";
import EditChildModal from "@/components/EditChildModal";

const RULE_LABELS = {
  trusted_school: { label: "Trusted school", icon: GraduationCap, tint: "var(--fcc-emma)" },
  trusted_coach: { label: "Trusted coach", icon: Trophy, tint: "var(--fcc-peach)" },
  trusted_camp: { label: "Trusted camp", icon: Tent, tint: "var(--fcc-noah)" },
  trusted_activity: { label: "Trusted activity", icon: Music, tint: "var(--fcc-green-soft)" },
  blocked: { label: "Blocked", icon: ShieldOff, tint: "var(--fcc-red-soft)" },
};

export default function ChildrenPage() {
  const [children, setChildren] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({ sender_email: "", sender_name: "", rule: "trusted_school" });

  const [childDraft, setChildDraft] = useState({ name: "", grade: "", color: "emma" });
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState("");
  const [editing, setEditing] = useState(null);
  const [loadingDemo, setLoadingDemo] = useState(false);
  const [resetting, setResetting] = useState(false);

  // "Reset demo data" only makes sense if demo rows actually exist.
  const hasDemoData = children.some((k) => k.is_demo);

  const load = async () => {
    setLoading(true);
    try {
      const [c, r] = await Promise.all([fetchChildren(), fetchSenderRules()]);
      setChildren(c);
      setRules(r);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const addChild = async (e) => {
    e.preventDefault();
    if (!childDraft.name.trim()) return;
    try {
      await createChild({ ...childDraft, activities: [] });
      toast.success(`${childDraft.name} added.`);
      setChildDraft((prev) => ({ ...prev, name: "", grade: "" }));
      load();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 402 && detail?.code === "plan_limit_reached") {
        setUpgradeReason(detail.message || "");
        setShowUpgrade(true);
      } else {
        toast.error(detail?.message || detail || "Could not add child.");
      }
    }
  };

  const removeChild = async (k) => {
    if (!window.confirm(`Remove ${k.name} from KidHQ? This can be re-added anytime.`)) return;
    try {
      await deleteChild(k.id);
      toast.success(`${k.name} removed.`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete child.");
    }
  };

  const handleLoadDemo = async () => {
    setLoadingDemo(true);
    try {
      const r = await loadDemoFamily();
      toast.success(r?.loaded ? "Demo family loaded — Emma & Noah ready to explore."
                              : "Demo family already loaded.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load demo family.");
    } finally {
      setLoadingDemo(false);
    }
  };

  const handleResetDemo = async () => {
    if (!window.confirm("Reset to the demo family? This wipes your current children, emails, tasks, and calendar.")) return;
    setResetting(true);
    try {
      await resetMyDemo();
      toast.success("Demo data reset.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset.");
    } finally {
      setResetting(false);
    }
  };

  const addRule = async (e) => {
    e.preventDefault();
    if (!draft.sender_email) return;
    await createSenderRule(draft);
    setDraft({ sender_email: "", sender_name: "", rule: "trusted_school" });
    toast.success("Sender rule saved");
    load();
  };

  const removeRule = async (id) => {
    await deleteSenderRule(id);
    toast("Rule removed");
    load();
  };

  const isEmpty = !loading && children.length === 0;

  return (
    <div data-testid={CHILDREN.page} className="space-y-10">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="label-overline">Children & senders</p>
          <h1 className="font-display text-4xl text-[var(--fcc-ink)] mt-2">Your family + who the AI listens to</h1>
        </div>
        {hasDemoData && (
          <button
            data-testid={CHILDREN.resetDemoBtn}
            onClick={handleResetDemo}
            disabled={resetting}
            title="QA: wipe all data and reload the demo family"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-white border border-[var(--fcc-border)] text-[var(--fcc-muted)] hover:text-[var(--fcc-ink)] hover:bg-[var(--fcc-cream-2)] disabled:opacity-50"
          >
            {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
            Reset demo data
          </button>
        )}
      </header>

      {isEmpty ? (
        <section
          data-testid={CHILDREN.emptyState}
          className="fcc-card p-10 text-center max-w-2xl mx-auto"
        >
          <div className="w-16 h-16 mx-auto rounded-3xl bg-[var(--fcc-cream-2)] flex items-center justify-center">
            <Users className="w-7 h-7 text-[var(--fcc-green)]" />
          </div>
          <h2 className="font-display text-3xl text-[var(--fcc-ink)] mt-5">Add your first child</h2>
          <p className="text-[var(--fcc-ink-soft)] mt-3 max-w-md mx-auto leading-relaxed">
            KidHQ keeps the mental load of school, sports, camps and activities organized by child.
            Add yours below, or explore with a demo family first.
          </p>
          <form onSubmit={addChild} className="grid sm:grid-cols-3 gap-3 mt-7 text-left">
            <input
              data-testid={CHILDREN.addNameInput}
              required
              className="fcc-input sm:col-span-2"
              placeholder="Child's name"
              value={childDraft.name}
              onChange={(e) => setChildDraft({ ...childDraft, name: e.target.value })}
            />
            <input
              data-testid={CHILDREN.addGradeInput}
              className="fcc-input"
              placeholder="Grade (e.g. 3)"
              value={childDraft.grade}
              onChange={(e) => setChildDraft({ ...childDraft, grade: e.target.value })}
            />
            <button
              data-testid={CHILDREN.emptyAddBtn}
              type="submit"
              className="fcc-btn-primary justify-center sm:col-span-3"
            >
              <Plus className="w-4 h-4" /> Add your first child
            </button>
          </form>
          <div className="mt-6 flex items-center gap-3 justify-center text-sm text-[var(--fcc-muted)]">
            <span className="flex-1 border-t border-[var(--fcc-border)]" />
            <span>or</span>
            <span className="flex-1 border-t border-[var(--fcc-border)]" />
          </div>
          <button
            data-testid={CHILDREN.emptyLoadDemoBtn}
            onClick={handleLoadDemo}
            disabled={loadingDemo}
            className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold bg-[var(--fcc-cream-2)] border border-[var(--fcc-border)] text-[var(--fcc-ink)] hover:bg-white disabled:opacity-50"
          >
            {loadingDemo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 text-[var(--fcc-green)]" />}
            Load demo family
          </button>
          <p className="text-xs text-[var(--fcc-muted)] mt-3">
            Adds Emma &amp; Noah with sample emails. We&apos;ll remove them automatically the moment you add a real child.
          </p>
        </section>
      ) : (
        <>
          <section className="fcc-card p-7">
            <p className="label-overline flex items-center gap-2"><UserPlus className="w-3 h-3" /> Add a child</p>
            <h2 className="font-display text-2xl text-[var(--fcc-ink)] mt-2">One more kid to track</h2>
            <p className="text-sm text-[var(--fcc-ink-soft)] mt-1">Free accounts include 1 child. Family Plus unlocks the whole crew.</p>
            <form onSubmit={addChild} className="grid md:grid-cols-4 gap-3 mt-5">
              <input
                data-testid={CHILDREN.addNameInput}
                required
                className="fcc-input md:col-span-2"
                placeholder="Child's name"
                value={childDraft.name}
                onChange={(e) => setChildDraft({ ...childDraft, name: e.target.value })}
              />
              <input
                data-testid={CHILDREN.addGradeInput}
                className="fcc-input"
                placeholder="Grade (e.g. 3)"
                value={childDraft.grade}
                onChange={(e) => setChildDraft({ ...childDraft, grade: e.target.value })}
              />
              <button
                data-testid={CHILDREN.addBtn}
                type="submit"
                className="fcc-btn-primary justify-center"
              >
                <Plus className="w-4 h-4" /> Add child
              </button>
            </form>
          </section>

          <section className="grid md:grid-cols-2 gap-6">
            {children.map((k) => (
              <article
                key={k.id}
                data-testid={CHILDREN.card(k.id)}
                data-demo={k.is_demo ? "true" : "false"}
                className="fcc-card p-7 relative"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <span className={`fcc-pill ${childChip(k.name)}`}>{k.grade}</span>
                    {k.is_demo && (
                      <span className="fcc-pill ml-2 bg-amber-100 text-amber-900">Demo</span>
                    )}
                    <h2 className="font-display text-3xl text-[var(--fcc-ink)] mt-3">{k.name}</h2>
                    <p className="text-sm text-[var(--fcc-ink-soft)] mt-1">{k.school}</p>
                  </div>
                  <span className="w-12 h-12 rounded-2xl bg-[var(--fcc-cream-2)] flex items-center justify-center">
                    <Users className="w-5 h-5 text-[var(--fcc-green)]" />
                  </span>
                </div>
                <p className="label-overline mt-6">Activities</p>
                <ul className="mt-3 flex flex-wrap gap-2">
                  {k.activities?.map((a) => (
                    <li key={a} className="fcc-pill bg-[var(--fcc-cream-2)] text-[var(--fcc-ink)]">{a}</li>
                  ))}
                </ul>
                <p className="label-overline mt-6">Notes</p>
                <p className="text-sm text-[var(--fcc-ink-soft)] mt-2">{k.notes || "No notes."}</p>

                <div className="flex items-center gap-2 mt-6 pt-5 border-t border-[var(--fcc-border)]">
                  <Link
                    to={`/app/children/${k.id}`}
                    data-testid={`child-open-profile-${k.id}`}
                    className="fcc-btn-primary text-sm py-2 px-3"
                  >
                    Open profile →
                  </Link>
                  <button
                    data-testid={CHILDREN.editBtn(k.id)}
                    onClick={() => setEditing(k)}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold text-[var(--fcc-ink)] hover:bg-[var(--fcc-cream-2)]"
                  >
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button
                    data-testid={CHILDREN.deleteBtn(k.id)}
                    onClick={() => removeChild(k)}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold text-rose-600 hover:bg-rose-50"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              </article>
            ))}
          </section>
        </>
      )}

      <section className="fcc-card p-7">
        <p className="label-overline">Sender rules</p>
        <h2 className="font-display text-2xl text-[var(--fcc-ink)] mt-2">Trusted &amp; blocked senders</h2>
        <p className="text-sm text-[var(--fcc-ink-soft)] mt-1">Teach the AI which senders always belong in the family inbox — and which to ignore.</p>

        <form onSubmit={addRule} className="grid md:grid-cols-4 gap-3 mt-6">
          <input className="fcc-input md:col-span-1" placeholder="Sender name" value={draft.sender_name} onChange={(e) => setDraft({ ...draft, sender_name: e.target.value })} />
          <input required type="email" className="fcc-input md:col-span-2" placeholder="sender@example.com" value={draft.sender_email} onChange={(e) => setDraft({ ...draft, sender_email: e.target.value })} />
          <div className="md:col-span-1 flex gap-2">
            <select className="fcc-input flex-1" value={draft.rule} onChange={(e) => setDraft({ ...draft, rule: e.target.value })}>
              {Object.entries(RULE_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <button data-testid={CHILDREN.ruleAdd} className="fcc-btn-primary px-4" type="submit"><Plus className="w-4 h-4" /></button>
          </div>
        </form>

        <ul className="mt-6 space-y-2">
          {rules.map((r) => {
            const meta = RULE_LABELS[r.rule] || RULE_LABELS.trusted_activity;
            const Icon = meta.icon;
            return (
              <li key={r.id} className="border border-[var(--fcc-border)] rounded-2xl p-4 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: meta.tint }}>
                    <Icon className="w-4 h-4 text-[var(--fcc-green)]" />
                  </span>
                  <div>
                    <p className="font-semibold text-[var(--fcc-ink)]">{r.sender_name || r.sender_email}</p>
                    <p className="text-xs text-[var(--fcc-muted)]">{r.sender_email} · {meta.label}</p>
                  </div>
                </div>
                <button data-testid={CHILDREN.ruleDelete} onClick={() => removeRule(r.id)} className="text-[var(--fcc-muted)] hover:text-[var(--fcc-red)] p-2"><Trash2 className="w-4 h-4" /></button>
              </li>
            );
          })}
          {rules.length === 0 && <li className="text-sm text-[var(--fcc-muted)]">No rules yet. Add a sender above.</li>}
        </ul>
      </section>

      <UpgradeModal open={showUpgrade} onClose={() => setShowUpgrade(false)} reason={upgradeReason} />
      <EditChildModal
        open={Boolean(editing)}
        child={editing}
        onClose={() => setEditing(null)}
        onSaved={() => load()}
      />
    </div>
  );
}
